import React, { useMemo, useState } from "react";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import { API_ORIGIN } from "../services/api";

const UserComment = ({
  data,
  history = [],
  onChange,
  currentUser,
  role,
  disabled = false,
  onResend,
  assignedUsers = []
}) => {
  const [preview, setPreview] = useState(null);
  const isQuality =
    role === "Admin" ||
    role === "Quality Manager" ||
    role === "Quality Staff";

  const BASE_URL = API_ORIGIN;

  const getId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value._id || value.id || "";
  };

  const getName = (value, fallback = "User") => {
    if (!value || typeof value === "string") return fallback;
    return value.username || value.name || fallback;
  };

  const getCommentOwnerId = (comment) =>
    getId(comment.targetUserId || comment.userId || comment.createdBy);

  const getCommentOwnerName = (comment, fallback = "User") =>
    getName(comment.targetUserId || comment.userId || comment.createdBy, fallback);

  const getAuthorLabel = (comment, isResent) => {
    if (isQuality) {
      if (isResent) return `Resent by ${getName(comment.createdBy, "Quality")}`;
      return getName(comment.userId || comment.createdBy, "User");
    }

    if (role === "Staff") {
      return isResent ? getName(comment.createdBy, "Quality") : "You";
    }

    if (isResent) return `Resent by ${getName(comment.createdBy, "Quality")}`;
    return getName(comment.userId || comment.createdBy, "User");
  };

  const handleChange = (e) => {
    onChange({ ...data, comment: e.target.value });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const formatted = files.map((file) => ({
      fileName: file.name,
      fileObj: file
    }));

    onChange({
      ...data,
      attachments: [...(data.attachments || []), ...formatted]
    });
  };

  const removeFile = (index) => {
    const updated = [...(data.attachments || [])];
    updated.splice(index, 1);
    onChange({ ...data, attachments: updated });
  };

  const filtered = history.filter((comment) => {
    if (role === "Staff") {
      return (
        getId(comment.userId) === currentUser ||
        getId(comment.targetUserId) === currentUser
      );
    }

    return true;
  });

  const rootComments = filtered.filter((comment) => !comment.parentId);
  const repliesByParent = useMemo(() => {
    return filtered.reduce((grouped, comment) => {
      const parentId = getId(comment.parentId);
      if (!parentId) return grouped;
      return {
        ...grouped,
        [parentId]: [...(grouped[parentId] || []), comment]
      };
    }, {});
  }, [filtered]);
  const latestReplyByUser = useMemo(() => {
    const latest = {};
    filtered
      .filter((comment) => comment.status !== "Resent")
      .forEach((comment) => {
        const userId = getId(comment.userId || comment.createdBy);
        const createdTime = new Date(comment.createdDate).getTime();
        if (!latest[userId] || createdTime > latest[userId].time) {
          latest[userId] = { id: getId(comment._id), time: createdTime };
        }
      });
    return latest;
  }, [filtered]);

  const latestResendByUser = useMemo(() => {
    const latest = {};
    filtered
      .filter((comment) => comment.status === "Resent")
      .forEach((comment) => {
        const userId = getId(comment.targetUserId || comment.userId);
        const createdTime = new Date(comment.createdDate).getTime();
        if (!latest[userId] || createdTime > latest[userId]) {
          latest[userId] = createdTime;
        }
      });
    return latest;
  }, [filtered]);

  const groupedThreads = (() => {
    const groups = {};

    assignedUsers.forEach((user) => {
      const id = getId(user);
      if (!id) return;
      groups[id] = {
        id,
        name: getName(user, "User"),
        rootComments: []
      };
    });

    rootComments.forEach((comment) => {
      const userId = getCommentOwnerId(comment);
      if (!userId) return;
      groups[userId] = groups[userId] || {
        id: userId,
        name: getCommentOwnerName(comment),
        rootComments: []
      };
      groups[userId].rootComments.push(comment);
    });

    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const getGroupStatus = (userId) => {
    const latestReply = latestReplyByUser[userId];
    const latestResend = latestResendByUser[userId];

    if (latestResend && (!latestReply || latestResend > latestReply.time)) {
      return { label: "Waiting for reply", className: "bg-warning text-dark" };
    }

    if (latestReply) {
      return { label: "Replied", className: "bg-success" };
    }

    return { label: "Pending", className: "bg-secondary" };
  };

  const getPreviewFiles = (attachments = []) =>
    attachments.map((file) => ({
      fileName: file.fileName,
      url: `${BASE_URL}${file.fileUrl}`
    }));

  const renderAttachments = (attachments = []) => {
    if (!attachments.length) return null;
    const previewFiles = getPreviewFiles(attachments);

    return (
      <div className="comment-attachments">
        {attachments.map((file, index) => (
          <button
            key={`${file.fileUrl || file.fileName}-${index}`}
            className="btn btn-outline-secondary btn-sm"
            type="button"
            onClick={() => setPreview({ files: previewFiles, index })}
          >
            <i className="bi bi-paperclip" aria-hidden="true"></i>
            <span>{file.fileName}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderCommentNode = (comment, depth = 0) => {
    const commentId = getId(comment._id);
    const isRoot = depth === 0;
    const isResent = comment.status === "Resent";
    const userId = getCommentOwnerId(comment);
    const latestUserReply = latestReplyByUser[userId];
    const hasPendingResend =
      latestResendByUser[userId] &&
      (!latestUserReply || latestResendByUser[userId] > latestUserReply.time);
    const canResend = Boolean(onResend) && isQuality && !isResent && latestUserReply?.id === commentId && !hasPendingResend;
    const replies = repliesByParent[commentId] || [];

    return (
      <div
        key={commentId}
        className={`${isRoot ? "comment-card" : "comment-reply"} ${isResent ? "resent" : ""}`}
      >
        <div className="comment-meta">
          <strong>{getAuthorLabel(comment, isResent)}</strong>
          <span>{comment.createdDate ? new Date(comment.createdDate).toLocaleString() : ""}</span>
        </div>

        {isResent && <span className="badge bg-warning text-dark mb-2">Resent</span>}

        <div className="comment-body">
          {comment.comment || comment.remarkFromQuality}
        </div>

        {comment.remarkFromQuality && comment.comment !== comment.remarkFromQuality && (
          <div className="alert alert-warning py-2 mb-2">
            Remark: {comment.remarkFromQuality}
          </div>
        )}

        {renderAttachments(comment.attachments)}

        {canResend && (
          <button
            className="btn btn-sm btn-warning mt-2 resend-action-btn"
            type="button"
            onClick={() =>
              onResend({
                type: "user",
                targetId: userId,
                parentId: commentId
              })
            }
          >
            <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
            <span>Resend Comment</span>
          </button>
        )}

        {replies.map((reply) => renderCommentNode(reply, depth + 1))}
      </div>
    );
  };

  return (
      <div className="form-container compact">
      <div className="comment-group-list">
        {groupedThreads.length === 0 && (
          <div className="text-muted small">No user comments yet.</div>
        )}

        {groupedThreads.map((group) => {
          const groupStatus = getGroupStatus(group.id);

          return (
            <div key={group.id} className="comment-group">
              <div className="comment-group-header">
                <div>
                  <div className="comment-group-title">{group.name}</div>
                  <div className="comment-group-subtitle">User comment thread</div>
                </div>
                <span className={`badge ${groupStatus.className}`}>{groupStatus.label}</span>
              </div>

              <div className="comment-timeline">
                {group.rootComments.length === 0 ? (
                  <div className="text-muted small">No reply received yet.</div>
                ) : (
                  group.rootComments.map((parent) => renderCommentNode(parent))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!disabled && (
        <div className="border rounded p-3 mt-3">
          <label className="fw-bold mb-2">Your Comment</label>
          <textarea
            value={data.comment || ""}
            onChange={handleChange}
            className="form-control"
            rows={4}
          />

          <label className="upload-box">
            <i className="bi bi-paperclip" aria-hidden="true"></i>
            <span>Attach files</span>
            <input type="file" multiple onChange={handleFileChange} />
          </label>

          {data.attachments?.map((file, index) => (
            <div key={`${file.fileName}-${index}`} className="upload-chip">
              <span>{file.fileName}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                title="Remove file"
                aria-label={`Remove ${file.fileName}`}
              >
                <i className="bi bi-trash" aria-hidden="true"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <AttachmentPreviewModal
        file={preview?.files?.[preview.index]}
        files={preview?.files || []}
        currentIndex={preview?.index || 0}
        onNavigate={(index) => setPreview((current) => ({ ...current, index }))}
        onClose={() => setPreview(null)}
      />
    </div>
  );
};

export default UserComment;
